import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { SensitiveDataReport as Report } from '@/types/data-table'

interface SensitiveDataReportProps {
  report: Report
}

/**
 * SensitiveDataReport displays findings from sensitive data detection
 *
 * Shows:
 * - Total findings count
 * - Breakdown by confidence level
 * - Individual findings with patterns and suggestions
 */
export function SensitiveDataReport({ report }: SensitiveDataReportProps) {
  const hasFindings = report.totalFindings > 0

  return (
    <div className="space-y-4">
      {/* Summary alert */}
      {hasFindings ? (
        <Alert variant="default" className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <AlertTitle className="text-amber-900 dark:text-amber-200">
            Sensitive Data Detected
          </AlertTitle>
          <AlertDescription className="text-amber-800 dark:text-amber-300">
            Found {report.totalFindings} potential sensitive data{' '}
            {report.totalFindings === 1 ? 'column' : 'columns'} in {report.tableName}
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className="border-green-200 bg-green-50 dark:bg-green-950/20">
          <CheckCircle2 className="h-5 w-5 text-green-600" />
          <AlertTitle className="text-green-900 dark:text-green-200">
            No Sensitive Data Detected
          </AlertTitle>
          <AlertDescription className="text-green-800 dark:text-green-300">
            No obvious sensitive data patterns found in {report.tableName}
          </AlertDescription>
        </Alert>
      )}

      {/* Confidence breakdown */}
      {hasFindings && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Confidence Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm">High Confidence</span>
              <Badge variant="destructive">
                {report.highConfidenceCount}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Medium Confidence</span>
              <Badge variant="default" className="bg-amber-500">
                {report.mediumConfidenceCount}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Low Confidence</span>
              <Badge variant="secondary">
                {report.lowConfidenceCount}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Findings list */}
      {hasFindings && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Findings</h3>
          {report.findings.map((finding, index) => (
            <Card key={index}>
              <CardContent className="pt-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="font-medium">{finding.columnName}</div>
                    <div className="text-sm text-muted-foreground">
                      {finding.dataType}
                    </div>
                  </div>
                  <Badge
                    variant={
                      finding.confidence >= 0.8
                        ? 'destructive'
                        : finding.confidence >= 0.5
                          ? 'default'
                          : 'secondary'
                    }
                  >
                    {Math.round(finding.confidence * 100)}% confident
                  </Badge>
                </div>

                <div className="space-y-1 text-sm">
                  <div>
                    <span className="text-muted-foreground">Pattern:</span>{' '}
                    <code className="bg-muted px-1 py-0.5 rounded">
                      {finding.pattern}
                    </code>
                  </div>
                  {finding.sampleCount > 0 && (
                    <div>
                      <span className="text-muted-foreground">
                        Sample matches:
                      </span>{' '}
                      {finding.sampleCount}
                    </div>
                  )}
                  {finding.suggestedClassification && (
                    <div>
                      <span className="text-muted-foreground">
                        Suggested classification:
                      </span>{' '}
                      <span className="font-medium">
                        {finding.suggestedClassification}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
